/**
 * /admin/filters — manage keyword patterns that drop ad/spam messages
 * before they hit raw_messages.
 *
 * Pattern UX: user types e.g. "这 条 广告" → 3 tokens, each must appear
 * IN ORDER in the message text (gaps allowed). Hint text walks operator
 * through the semantics with an example.
 */

import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FilterRule, type MessageSample, Routes } from '@tg-leads/contracts';

import { api } from '../../lib/api';
import { friendlyError } from '../../lib/friendlyError';

interface FilterList {
  items: FilterRule[];
}
interface SampleList {
  items: MessageSample[];
}

export function FiltersPage() {
  const qc = useQueryClient();
  const [pattern, setPattern] = useState('');
  const [addErr, setAddErr] = useState<string | null>(null);
  const patternInputRef = useRef<HTMLInputElement>(null);

  const filters = useQuery({
    queryKey: ['filters'],
    queryFn: () => api.get<FilterList>(Routes.admin.filters),
  });

  const createMut = useMutation({
    mutationFn: (p: string) =>
      api.post<FilterRule>(Routes.admin.filters, { pattern: p }),
    onSuccess: () => {
      setPattern('');
      setAddErr(null);
      qc.invalidateQueries({ queryKey: ['filters'] });
    },
    onError: (e: unknown) =>
      setAddErr(friendlyError(e)),
  });

  const toggleMut = useMutation({
    mutationFn: (vars: { id: string; enabled: boolean }) =>
      api.put<FilterRule>(Routes.admin.filter(vars.id), { enabled: vars.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['filters'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(Routes.admin.filter(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['filters'] }),
  });

  // Samples — lives in memory on server, lost on restart. Poll every 5s so
  // admin sees fresh spam land in real time without clicking refresh.
  const samples = useQuery({
    queryKey: ['filterSamples'],
    queryFn: () => api.get<SampleList>(Routes.admin.filterSamples),
    refetchInterval: 5_000,
  });

  const clearSamplesMut = useMutation({
    mutationFn: () => api.delete(Routes.admin.filterSamples),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['filterSamples'] }),
  });

  const fillPattern = (text: string) => {
    setPattern(text);
    patternInputRef.current?.focus();
    patternInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Dry-run preview: as user types a pattern, run the same
  // matchesSequentialTokens logic the server uses (duplicated client-side
  // because the algorithm is 5 lines and async round-trip per keystroke is
  // overkill). Shows count + top 5 matched samples.
  const dryRun = useMemo(() => {
    const tokens = pattern
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (tokens.length === 0 || !samples.data?.items?.length) {
      return { matchCount: 0, matchedSamples: [] as MessageSample[] };
    }
    const matched = samples.data.items.filter((s) =>
      matchesSequentialTokensLocal(s.text, tokens),
    );
    return {
      matchCount: matched.length,
      matchedSamples: matched.slice(0, 5),
    };
  }, [pattern, samples.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">关键词过滤</h1>
        <p className="text-sm text-slate-500 mt-1">
          命中规则的消息会被直接丢弃，不入库、不进管线，省下后面所有的 LLM 调用成本。
        </p>
      </div>

      <div className="card space-y-3">
        <h2 className="font-medium text-slate-700">添加规则</h2>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            const p = pattern.trim();
            if (!p) return;
            createMut.mutate(p);
          }}
          className="flex gap-2"
        >
          <input
            ref={patternInputRef}
            className="input flex-1"
            placeholder="空格分隔多个关键词，按顺序匹配。例如：这 条 广告"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
          />
          <button
            type="submit"
            className="btn-primary"
            disabled={createMut.isPending || !pattern.trim()}
          >
            {createMut.isPending ? '添加中…' : '添加'}
          </button>
        </form>

        {pattern.trim() && samples.data?.items?.length ? (
          <div className="text-xs bg-indigo-50 border border-indigo-100 rounded p-3 space-y-2">
            <div>
              <span className="font-medium text-indigo-700">
                命中预览：
              </span>
              如果现在保存，这条规则会命中{' '}
              <span className="font-bold tabular-nums">
                {dryRun.matchCount}
              </span>{' '}
              / {samples.data.items.length} 条样本
              {dryRun.matchCount === 0
                ? '（没匹配到，要不调一下关键词？）'
                : dryRun.matchCount > 10
                  ? '（命中比例偏高，可能拦得太宽）'
                  : ''}
            </div>
            {dryRun.matchedSamples.length > 0 && (
              <ul className="text-[11px] text-slate-700 space-y-1 max-h-32 overflow-y-auto">
                {dryRun.matchedSamples.map((s, i) => (
                  <li key={i} className="border-l-2 border-indigo-300 pl-2">
                    <span className="text-slate-400 font-mono">
                      ×{s.count}
                    </span>{' '}
                    {s.text.slice(0, 100)}
                    {s.text.length > 100 && '…'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <div className="text-xs text-slate-500 bg-slate-50 rounded p-3 leading-relaxed">
          <p className="font-medium mb-1">规则说明（多关键词按顺序匹配，允许中间夹字）</p>
          <p>
            规则 <code className="font-mono text-indigo-700">这 条 广</code>{' '}
            会匹配 <code className="font-mono">这是一条广告</code>（"这"→"条"→"广" 按顺序出现）；
          </p>
          <p>
            但不会匹配 <code className="font-mono">广告这条</code>（顺序错了）。
          </p>
          <p>单个关键词（中间没空格）就是子串匹配。</p>
        </div>
        {addErr && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
            {addErr}
          </div>
        )}
      </div>

      <div className="card overflow-x-auto">
        {filters.isLoading ? (
          <div className="text-slate-500 text-sm">加载中…</div>
        ) : filters.error ? (
          <div className="text-red-600 text-sm">
            {(filters.error as Error).message}
          </div>
        ) : !filters.data?.items.length ? (
          <div className="text-slate-500 text-sm">
            还没有规则。在上面框里添加第一条。
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase border-b">
                <th className="py-2 pr-4">规则</th>
                <th className="py-2 pr-4">启用</th>
                <th className="py-2 pr-4">添加时间</th>
                <th className="py-2 pr-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {filters.data.items.map((f) => (
                <tr key={f.id} className="border-b last:border-0">
                  <td className="py-3 pr-4 font-mono">{f.pattern}</td>
                  <td className="py-3 pr-4">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={f.enabled}
                        disabled={toggleMut.isPending}
                        onChange={(e) =>
                          toggleMut.mutate({
                            id: f.id,
                            enabled: e.target.checked,
                          })
                        }
                      />
                      <span className="ml-2 text-xs text-slate-600">
                        {f.enabled ? '启用' : '停用'}
                      </span>
                    </label>
                  </td>
                  <td className="py-3 pr-4 text-xs text-slate-500">
                    {new Date(f.createdAt).toLocaleString()}
                  </td>
                  <td className="py-3 pr-4">
                    <button
                      className="btn-danger"
                      disabled={deleteMut.isPending}
                      onClick={() => {
                        if (confirm(`删除规则 "${f.pattern}"?`)) {
                          deleteMut.mutate(f.id);
                        }
                      }}
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card overflow-x-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-medium text-slate-700">最近的消息样本</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              这是已通过过滤、进了数据库的消息样本。重复次数越多说明越值得加成规则。点任一行直接把内容填进上面的规则框。
              （内存保存，重启会清空；每 5 秒自动刷新；按次数倒序）
            </p>
          </div>
          <button
            className="btn-secondary text-xs"
            disabled={clearSamplesMut.isPending || !samples.data?.items.length}
            onClick={() => {
              if (confirm('清空样本缓存（不影响已保存的规则）?')) clearSamplesMut.mutate();
            }}
          >
            清空样本
          </button>
        </div>
        {samples.isLoading ? (
          <div className="text-slate-500 text-sm">加载中…</div>
        ) : !samples.data?.items.length ? (
          <div className="text-slate-500 text-sm">
            还没收到消息。等 TG 账号采集几条，或者去群里发条消息试试。
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase border-b">
                <th className="py-2 pr-4 w-16">出现次数</th>
                <th className="py-2 pr-4">消息内容（点击填入规则框）</th>
                <th className="py-2 pr-4 w-40">来源群</th>
                <th className="py-2 pr-4 w-32">最近看到</th>
              </tr>
            </thead>
            <tbody>
              {samples.data.items.map((s, i) => (
                <tr
                  key={i}
                  className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                  onClick={() => fillPattern(s.text)}
                >
                  <td className="py-2 pr-4 font-mono tabular-nums">
                    <span
                      className={
                        s.count >= 10
                          ? 'text-red-600 font-medium'
                          : s.count >= 3
                            ? 'text-orange-600'
                            : 'text-slate-700'
                      }
                    >
                      {s.count}
                    </span>
                  </td>
                  <td className="py-2 pr-4 max-w-md">
                    <div className="whitespace-pre-wrap break-words line-clamp-3 text-xs">
                      {s.text}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-xs text-slate-500 truncate max-w-[10rem]">
                    {s.lastGroupTitle ?? '—'}
                  </td>
                  <td className="py-2 pr-4 text-xs text-slate-500">
                    {new Date(s.lastSeenAt).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * Mirror of server-side matchesSequentialTokens. Duplicated rather than
 * fetched per-keystroke because the algorithm is trivial and a round-trip
 * on every key press would be silly. Keep in sync with the server impl in
 * apps/server/src/modules/filter/filter.service.ts.
 */
function matchesSequentialTokensLocal(text: string, tokens: string[]): boolean {
  if (tokens.length === 0) return false;
  let cursor = 0;
  for (const tok of tokens) {
    const idx = text.indexOf(tok, cursor);
    if (idx === -1) return false;
    cursor = idx + tok.length;
  }
  return true;
}
